<script>
import { mapActions } from 'vuex'
import LogDetailsMixin from './log-details.mixin.js'
import capitalize from 'lodash/capitalize'

const underscoreRE = /[_-]/g

export default {
  name: 'MerchantLogDetails',
  mixins: [LogDetailsMixin],
  data() {
    return {
      loading: false,
      loadedMerchant: null,
    }
  },
  computed: {
    merchant() {
      const { loadedMerchant, metadata: { merchant } } = this

      if (loadedMerchant) {
        return loadedMerchant
      }

      if (merchant) {
        if (merchant instanceof Array) {
          return {}
        }

        return merchant
      }

      return {}
    },
    feedMerchant() {
      const { metadata, merchant } = this

      return metadata.feed_merchant || merchant.default_feed_merchant || {}
    },
    changedPropNames() {
      return Object
        .keys(this.merchant)
        .map(name => capitalize(name.replace(underscoreRE, ' ')))
    },
    changedPropValues() {
      const { merchant } = this
      const values = []

      for (const key in merchant) {
        if (merchant.hasOwnProperty(key)) {
          values.push(merchant[key])
        }
      }

      return values
    },
  },
  async created() {
    if (this.action === 'action_create_merchant') {
      this.loading = true
      const [, response] = await this.getGlobalMerchant(this.merchant.id)

      this.loadedMerchant = response
      this.loading = false
    }
  },
  methods: {
    ...mapActions('merchants', [
      'getGlobalMerchant',
      'getGlobalMerchantFeeds',
    ]),
    getBaseCommission() {
      const { map } = this.feedMerchant

      if (map) {
        const { commission } = map
        let { base, type } = commission

        if (type === 'PERCENTAGE') {
          base += '%'
        } else {
          base = '$' + base
        }

        return base
      }

      return '—'
    },
  },
}
</script>

<template>
  <expanded-row :columns="columns">
    <template v-if="action === 'action_create_merchant'">
      <div slot="expanded-row-cell-1">
        <div :class="$style.subrow">
          Global Merchant Name
        </div>
        <div :class="$style.subrow">
          Global Merchant Summary
        </div>
        <div :class="$style.subrow">
          Feed Merchant
        </div>
        <div :class="$style.subrow">
          Commission Aggregator
        </div>
        <div :class="$style.subrow">
          Commission Type
        </div>
        <div :class="$style.subrow">
          Tracking URL
        </div>
        <div :class="$style.subrow">
          Base Commission
        </div>
        <div :class="$style.subrow">
          Classifications
        </div>
      </div>

      <div slot="expanded-row-cell-2">
        <base-loader
          v-if="loading"
          theme="state"
        />
        <template v-else>
          <div :class="$style.subrow">
            {{ merchant.name || '—' }}
          </div>
          <div :class="$style.subrow">
            {{ merchant.summary || '—' }}
          </div>
          <div :class="$style.subrow">
            {{ feedMerchant.map ? feedMerchant.map.feed : '—' }}
          </div>
          <div :class="$style.subrow">
            {{ feedMerchant.map ? feedMerchant.map.feed : '—' }}
          </div>
          <div :class="$style.subrow">
            {{ feedMerchant.map ? feedMerchant.map.commission.type : '—' }}
          </div>
          <div :class="$style.subrow">
            -
          </div>
          <div :class="$style.subrow">
            {{ getBaseCommission() }}
          </div>
          <div :class="$style.subrow">
            {{
              merchant.categories
                ? merchant.categories.map(c => c.name).join(', ')
                : '—'
            }}
          </div>
        </template>
      </div>
    </template>

    <template v-if="action === 'action_delete_merchant'">
      <div slot="expanded-row-cell-1">
        <div :class="$style.subrow">
          Global Merchant Name
        </div>
      </div>

      <div slot="expanded-row-cell-2">
        <div :class="$style.subrow">
          {{ metadata.name }}
        </div>
      </div>
    </template>

    <template v-else-if="action === 'action_associate_feed_to_merchant'">
      <div slot="expanded-row-cell-1">
        <div :class="$style.subrow">
          Global Merchant Name
        </div>
        <div :class="$style.subrow">
          New Feed Merchant
        </div>
      </div>

      <div slot="expanded-row-cell-2">
        <div :class="$style.subrow">
          {{ merchant.name || '—' }}
        </div>
        <div :class="$style.subrow">
          {{ feedMerchant.map.name || '—' }}
        </div>
      </div>
    </template>

    <template
      v-else-if="
        action === 'action_activate_merchant' ||
          action === 'action_deactivate_merchant'
      "
    >
      <div slot="expanded-row-cell-1">
        <div :class="$style.subrow">
          Global Merchant Name
        </div>
        <div :class="$style.subrow">
          Status
        </div>
      </div>

      <div slot="expanded-row-cell-2">
        <div :class="$style.subrow">
          {{ merchant.name || metadata.name || '—' }}
        </div>
        <div :class="$style.subrow">
          {{ merchant.enabled ? 'Active' : 'Inactive' }}
        </div>
      </div>
    </template>

    <template v-else-if="action === 'action_update_merchant'">
      <div slot="expanded-row-cell-1">
        <div :class="$style.subrow">
          Global Merchant Name
        </div>
        <div
          v-for="name in changedPropNames"
          :key="name"
          :class="$style.subrow"
        >
          {{ name || '—' }}
        </div>
      </div>

      <div slot="expanded-row-cell-2">
        <div :class="$style.subrow">
          {{ metadata.name || '—' }}
        </div>
        <div
          v-for="(value, index) in changedPropValues"
          :key="index"
          :class="$style.subrow"
        >
          {{ value || '—' }}
        </div>
      </div>
    </template>

    <template v-else>
      <div slot="expanded-row-cell-1">
        No additional info
        <div />
      </div>
    </template>
  </expanded-row>
</template>

<style lang="scss" module>
  .subrow {
    margin-bottom: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
</style>
